import React from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Alert } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useGetAccount } from '~/hooks/apis/offers/useGetAccount';

import { formatDateSlashWithTime } from '~/services/TimeService';

import AccountListPageHeader from '../AccountListPageHeader';
import { useAccountRelatedFullList } from '../useAccountRelatedRows';

import { toAccountNoteAttachment } from './toAccountNoteAttachment';
import type { AccountNoteAttachment } from './types';

const sharedOpts: Partial<GridColDef<AccountNoteAttachment>> = {
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
};

const columns: Array<GridColDef<AccountNoteAttachment>> = [
  { field: 'title', headerName: 'Title', flex: 2, minWidth: 240, ...sharedOpts },
  { field: 'type', headerName: 'Type', flex: 1, minWidth: 120, ...sharedOpts },
  {
    field: 'lastModifiedDate',
    headerName: 'Last Modified',
    flex: 1,
    minWidth: 180,
    renderCell: ({ row }) => (row.lastModifiedDate ? formatDateSlashWithTime(row.lastModifiedDate) : '—'),
    ...sharedOpts,
  },
];

export default function AccountNotesAttachmentsListPage() {
  const { id_account: accountId } = useParams<{ id_account: string }>();
  const { data: account } = useGetAccount(accountId);
  const {
    count,
    error,
    isLoading,
    isTruncated,
    rows: notesAttachments,
  } = useAccountRelatedFullList(accountId, 'notes_attachments', toAccountNoteAttachment);

  return (
    <>
      <Helmet>
        <title>Notes & Attachments | {account?.name || accountId}</title>
      </Helmet>

      <AccountListPageHeader
        accountId={accountId}
        count={count}
        isTruncated={isTruncated}
        sortedBy="Last Modified"
        title="Notes & Attachments"
      />

      {error ? (
        <Alert severity="error">Could not load notes & attachments: {error.message}</Alert>
      ) : (
        <DataGrid
          autoHeight
          disableRowSelectionOnClick
          columns={columns}
          loading={isLoading}
          rows={notesAttachments}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
            sorting: { sortModel: [{ field: 'lastModifiedDate', sort: 'desc' }] },
          }}
          pageSizeOptions={[25, 50, 100]}
        />
      )}
    </>
  );
}
